import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import CreateableSelect from 'react-select/lib/Creatable';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import { FixedSizeList as List } from "react-window";

import extendedFormsStyle from "assets/jss/material-dashboard-pro-react/views/extendedFormsStyle.jsx";
import CustomInput from '../CustomInput/CustomInput';
import { CircularProgress } from '@material-ui/core';

const styles = theme => ({
    root: {
        flexGrow: 1,
        height: 250,
    },
    input: {
        display: 'flex',
        padding: 0,
    },
    valueContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
        overflow: 'hidden',
    },
    chip: {
        margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08,
        ),
    },
    avatarChildren: {
        height: 32,
        width: 32
    },
    noOptionsMessage: {
        padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
    },
    singleValue: {
        fontSize: 14,
    },
    placeholder: {
        position: 'absolute',
        left: 2,
        fontSize: 14,
        color: '#ccc'
    },
    paper: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing.unit,
        left: 0,
        right: 0,
    },
    divider: {
        height: theme.spacing.unit * 2,
    },
    ...extendedFormsStyle
});

var LoadingIndicator = (props) => <CircularProgress {...props} size={20} color="secondary" style={{ marginRight: 10, ...props.style }} />;

var NoOptionsMessage = withStyles(styles)((props) => {
    return (
        <Typography
            color="textSecondary"
            className={props.classes.noOptionsMessage}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
})

var Placeholder = withStyles(styles)((props) => {
    return (
        <Typography
            color="textSecondary"
            className={props.classes.placeholder}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
})

var SingleValue = withStyles(styles)((props) => {
    return (
        <Typography className={props.classes.singleValue} {...props.innerProps}>
            {props.children}
        </Typography>
    );
});

var ValueContainer = withStyles(styles)((props) => {
    return <div className={props.classes.valueContainer}>{props.children}</div>;
})

class CancelHover extends React.Component {
    state = {
        isHovering: false
    }
    render() {
        return <CancelIcon {...this.props} style={{ transition: 'color .25s cubic-bezier(0.4, 0.0, 0.2, 1)', color: this.state.isHovering ? '#fff' : 'rgba(255,255,255, 0.65)' }} onMouseOver={() => this.setState({ isHovering: true })} onMouseOut={() => this.setState({ isHovering: false })} />
    }
}

var MultiValue = withStyles(styles)((props) => {

    const { classes } = props;

    return (
        <Chip
            tabIndex={-1}
            label={props.children}
            className={classNames(props.classes.chip, {
                [props.classes.chipFocused]: props.isFocused,
            })}
            classes={{ avatarChildren: classes.avatarChildren }}
            onDelete={props.removeProps.onClick}
            deleteIcon={<CancelHover />}
            color="primary"
            {...(props.data.avatar ? { avatar: props.data.avatar } : {})}
        />
    );
})

var Menu = withStyles(styles)((props) => {
    return (
        <Paper
            className={props.classes.paper}
            {...props.innerProps}
        >
            {props.children}
        </Paper>
    );
});

var MenuList = (props) => {
    const { options, children, getValue } = props;
    var { maxHeight } = props;
    var height = props.selectProps.itemHeight || 48;
    const [value] = getValue();
    const initialOffset = options.indexOf(value) * height;

    if (children.length < 6 && children.length > 0)
        maxHeight = (maxHeight / 6) * children.length;
    else if (children.length === 0) {
        maxHeight = height;
    }

    return (
        <List
            height={maxHeight}
            itemCount={children.length}
            itemSize={height}
            initialScrollOffset={initialOffset}
        >
            {({ index, style }) => <div style={style}>{children[index]}</div>}
        </List>
    );
}

var Option = (props) => {
    const { children, isFocused, isSelected, onFocus } = props;
    const { classes } = props.selectProps;
    return (
        <MenuItem
            onFocus={onFocus}
            selected={isFocused}
            onClick={(e) => props.selectOption(props.data, e)}
            component="div"
            style={{
                fontWeight: isSelected ? 500 : 400,
                display: 'flex',
                flexDirection: 'row'
            }}
            classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected
            }}
        >
            {props.data.avatar ? props.data.avatar : null}
            <span style={{ flex: 1, ...(props.data.avatar ? { marginLeft: 20 } : {}) }}>{children}</span>
        </MenuItem>
    );
}

const wrapperStyles = {
    control: () => ({
        display: "flex",
        alignItems: "center",
        border: 0,
        height: "auto",
        background: "transparent",
        marginBottom: -4,
        "&:hover": {
            boxShadow: "none"
        }
    }),
};

function SelectWrapped(props) {
    const { isCreateable, ...rest } = props;

    var SelectComponent = Select;

    if (props.isCreateable)
        SelectComponent = CreateableSelect;


    return (
        <SelectComponent
            {...rest}
            styles={{ ...wrapperStyles, ...rest.styles }}
            components={{
                Option,
                Menu,
                MenuList,
                MultiValue,
                NoOptionsMessage,
                Placeholder,
                SingleValue,
                ValueContainer,
                LoadingIndicator
            }}

        />
    );
}

class ReactSelect extends React.Component {
    render() {
        const {
            classes,
            label,
            labelProps,
            formControlProps,
            inputProps,
            options,
            itemHeight,
            isMulti,
            isCreateable,
            isClearable,
            isDisabled,
            isLoading,
            isValidNewOption,
            value,
            inputValue,
            menuIsOpen,
            onKeyDown,
            onChange,
            onCreateOption,
            onInputChange,
            placeholder,
            ...rest
        } = this.props;
        return (
            <div className={classes.root}>
                <CustomInput
                    {...rest}

                    labelText={label}
                    fullWidth
                    formControlProps={{
                        fullWidth: true,
                        ...(formControlProps || {})
                    }}
                    labelProps={{ shrink: true, ...(labelProps || {}) }}
                    inputProps={{
                        inputComponent: SelectWrapped,
                        inputProps: {
                            ...(inputProps || {}),
                            options,
                            itemHeight,
                            classes,
                            isCreateable: isCreateable ? true : false,
                            isMulti: isMulti ? true : false,
                            isClearable: isClearable ? true : false,
                            isDisabled: isDisabled ? true : false,
                            isLoading: isLoading ? true : false,
                            value: value ? value : undefined,
                            onChange: onChange ? onChange : undefined,
                            onInputChange: onInputChange ? onInputChange : undefined,
                            onCreateOption: onCreateOption ? onCreateOption : undefined,
                            inputValue: inputValue ? inputValue : undefined,
                            menuIsOpen: menuIsOpen ? menuIsOpen : undefined,
                            onKeyDown: onKeyDown ? onKeyDown : undefined,
                            isValidNewOption: isValidNewOption ? isValidNewOption : undefined,
                            placeholder: placeholder ? placeholder : "Select...",
                        }
                    }}
                />
            </div>
        );
    }
}

ReactSelect.propTypes = {
    label: PropTypes.string,
    formControlProps: PropTypes.any,
    labelProps: PropTypes.any,
    inputProps: PropTypes.any,
    options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]).isRequired, avatar: PropTypes.any, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object, PropTypes.bool]).isRequired })).isRequired,
    isMulti: PropTypes.bool,
    isCreateable: PropTypes.bool,
    isClearable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    value: PropTypes.any,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    itemHeight: PropTypes.number,
    isValidNewOption: PropTypes.func,
    inputValue: PropTypes.any,
    menuIsOpen: PropTypes.bool,
    onKeyDown: PropTypes.func,
    onChange: PropTypes.func,
    onCreateOption: PropTypes.func,
    onInputChange: PropTypes.func,
}

export default withStyles({ ...extendedFormsStyle })(ReactSelect);